import React from "react";
import {
  Activity,
  ArrowUpDown,
  Calendar,
  CalendarClock,
  CalendarRange,
  Clock,
  Layers,
  LayoutGrid,
  SlidersHorizontal,
  Ticket,
  Trophy,
  Users,
  X,
  Zap,
} from "lucide-react";
import SidebarNavIcon from "../ui/SidebarNavIcon";
import {
  PROGRAM_SORT_OPTIONS,
  PROGRAM_TIME_FILTERS,
} from "../../utils/bettingDeskHelpers";

const TIME_ICONS = {
  all: LayoutGrid,
  today: Calendar,
  tomorrow: CalendarClock,
  week: CalendarRange,
  later: Clock,
};

const FilterSection = ({ label, labelIcon: LabelIcon, children }) => (
  <section className="pbd-nav-section">
    <p className="pbd-nav-section-label">
      {LabelIcon ? <LabelIcon size={10} strokeWidth={2.4} aria-hidden /> : null}
      {label}
    </p>
    <div className="pbd-nav-list">{children}</div>
  </section>
);

const NavFilterItem = ({ active, onClick, icon, logo, label, hint, disabled }) => (
  <button
    type="button"
    className={`pbd-nav-item${active ? " pbd-nav-item-on" : ""}${disabled ? " pbd-nav-item-disabled" : ""}`}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
  >
    {logo ? (
      <span className="pbd-nav-item-crest">
        <img src={logo} alt="" />
      </span>
    ) : (
      <SidebarNavIcon icon={icon} size={13} />
    )}
    <span className="pbd-nav-item-copy">
      <strong>{label}</strong>
      {hint ? <em className="tabular-nums">{hint}</em> : null}
    </span>
  </button>
);

const formatFixtureHint = (count) => {
  const n = Number(count) || 0;
  if (!n) return "None open";
  return `${n} fixture${n === 1 ? "" : "s"}`;
};

const SORT_ICONS = {
  kickoff: Clock,
  league: Trophy,
  marketsDesc: Layers,
  bettorsDesc: Users,
};

const ProgramFiltersRail = ({
  timeFilter,
  onTimeFilterChange,
  leagueId,
  onLeagueChange,
  leagues = [],
  timeCounts = {},
  onSlipOnly,
  onSlipOnlyChange,
  slipCount = 0,
  startingSoon,
  onStartingSoonChange,
  startingSoonCount = 0,
  popularOnly,
  onPopularOnlyChange,
  popularCount = 0,
  deepBoardOnly,
  onDeepBoardOnlyChange,
  deepBoardCount = 0,
  deepMarketsThreshold = 8,
  sort,
  onSortChange,
  onClear,
  activeCount = 0,
  resultCount = 0,
  compact,
  onClose,
}) => {
  const hasLeagues = leagues.length > 0;

  return (
    <aside className={`pbd-nav-rail${compact ? " pbd-nav-rail-compact" : ""}`}>
      <div className="pbd-nav-head">
        <div className="pbd-nav-title">
          <SlidersHorizontal size={14} strokeWidth={2.25} aria-hidden />
          <span>Program deck</span>
        </div>
        {compact && onClose ? (
          <button type="button" className="pbd-chrome-btn" onClick={onClose} aria-label="Close filters">
            <X size={14} />
          </button>
        ) : null}
      </div>

      <div className="pbd-nav-summary" aria-live="polite">
        <span className="pbd-nav-summary-count tabular-nums">{resultCount}</span>
        <span className="pbd-nav-summary-copy">
          {resultCount === 1 ? "fixture" : "fixtures"} in view
          {activeCount ? ` · ${activeCount} filter${activeCount === 1 ? "" : "s"} on` : ""}
        </span>
      </div>

      <div className="pbd-nav-scroll">
        <FilterSection label="Kickoff" labelIcon={Calendar}>
          {PROGRAM_TIME_FILTERS.map(({ id, label }) => (
            <NavFilterItem
              key={id}
              active={timeFilter === id}
              onClick={() => onTimeFilterChange?.(id)}
              icon={TIME_ICONS[id] || LayoutGrid}
              label={label}
              hint={formatFixtureHint(timeCounts[id])}
            />
          ))}
        </FilterSection>

        <FilterSection label="Spotlight" labelIcon={Zap}>
          <NavFilterItem
            active={startingSoon}
            onClick={() => onStartingSoonChange?.(!startingSoon)}
            icon={Clock}
            label="Starting soon"
            hint={startingSoonCount ? `${startingSoonCount} within 6h` : "Next 6 hours"}
          />
          <NavFilterItem
            active={onSlipOnly}
            disabled={slipCount === 0}
            onClick={() => onSlipOnlyChange?.(!onSlipOnly)}
            icon={Ticket}
            label="On slip only"
            hint={slipCount ? `${slipCount} leg${slipCount === 1 ? "" : "s"}` : "Add picks first"}
          />
        </FilterSection>

        <FilterSection label="Desk prediction" labelIcon={Activity}>
          <NavFilterItem
            active={popularOnly}
            onClick={() => onPopularOnlyChange?.(!popularOnly)}
            icon={Users}
            label="With bettors"
            hint={popularCount ? `${popularCount} active` : "No picks yet"}
          />
          <NavFilterItem
            active={deepBoardOnly}
            onClick={() => onDeepBoardOnlyChange?.(!deepBoardOnly)}
            icon={Layers}
            label="Deep market board"
            hint={
              deepBoardCount
                ? `${deepBoardCount} · ${deepMarketsThreshold}+ mkts`
                : `${deepMarketsThreshold}+ markets`
            }
          />
        </FilterSection>

        {hasLeagues ? (
          <FilterSection label="League" labelIcon={Trophy}>
            <NavFilterItem
              active={!leagueId}
              onClick={() => onLeagueChange?.("")}
              icon={LayoutGrid}
              label="All leagues"
              hint={`${leagues.reduce((s, l) => s + (l.count || 0), 0)} total`}
            />
            {leagues.map((league) => (
              <NavFilterItem
                key={league.id}
                active={leagueId === league.id}
                onClick={() => onLeagueChange?.(league.id)}
                logo={league.logo}
                label={league.name}
                hint={formatFixtureHint(league.count)}
              />
            ))}
          </FilterSection>
        ) : null}

        <FilterSection label="Order by" labelIcon={ArrowUpDown}>
          {PROGRAM_SORT_OPTIONS.map(({ id, label }) => (
            <NavFilterItem
              key={id}
              active={sort === id}
              onClick={() => onSortChange?.(id)}
              icon={SORT_ICONS[id] || Clock}
              label={label}
            />
          ))}
        </FilterSection>
      </div>

      {activeCount > 0 ? (
        <button type="button" className="pbd-nav-clear" onClick={onClear}>
          Reset filters
        </button>
      ) : null}
    </aside>
  );
};

export default ProgramFiltersRail;
